<template>
  <v-main>
    <v-container fluid>
      <!-- Professional Page Header -->
      <v-row class="mb-1">
        <v-col cols="12">
          <v-card elevation="2" class="page-header-card">
            <v-card-title class="py-1 px-2">
              <v-row class="w-100 ma-0" align="center">
                <!-- Left Side: Title and Description -->
                <v-col cols="12" md="8" class="pa-0">
                  <div class="d-flex align-center">
                    <div>
                      <h1 class="page-title primary--text">Edit Transaction</h1>
                      <p class="page-subtitle grey--text text--darken-1 mb-0">
                        Modify transaction details, payment information, and status
                      </p>
                    </div>
                  </div>
                </v-col>

                <!-- Right Side: Edit Toggle and Back Button -->
                <v-col cols="12" md="4" class="pa-0">
                  <div class="d-flex align-center justify-end">
                    <!-- Edit/Read-Only Toggle Button -->
                    <v-btn
                      :color="isEditMode ? 'warning' : 'primary'"
                      :outlined="!isEditMode"
                      @click="toggleEditMode"
                      class="mr-2"
                      small
                    >
                      <v-icon left>{{ isEditMode ? 'mdi-eye' : 'mdi-pencil' }}</v-icon>
                      {{ isEditMode ? 'Read Only' : 'Edit Mode' }}
                    </v-btn>
                    
                    <v-btn
                      color="grey darken-1"
                      outlined
                      @click="$router.back()"
                      class="back-button"
                      small
                    >
                      <v-icon left>mdi-arrow-left</v-icon>
                      Back to Transactions
                    </v-btn>
                  </div>
                </v-col>
              </v-row>
            </v-card-title>
          </v-card>
        </v-col>
      </v-row>

      <!-- Professional Form Card -->
      <v-row>
        <v-col cols="12">
          <v-card elevation="3" class="form-card">
            <!-- Form Header -->
            <v-card-title class="form-header py-1 px-2">
              <h2 class="form-title">Transaction Information</h2>
              <v-spacer></v-spacer>
              <v-chip 
                :color="isEditMode ? 'success' : 'warning'" 
                :outlined="!isEditMode" 
                x-small
              >
                <v-icon left x-small>{{ isEditMode ? 'mdi-pencil' : 'mdi-eye' }}</v-icon>
                {{ isEditMode ? 'Edit Mode' : 'Read Only Mode' }}
              </v-chip>
            </v-card-title>

            <v-divider></v-divider>
            <!-- Form Content -->
            <v-card-text class="form-content px-2 py-2">
              <!-- Transaction Details -->
              <v-card class="mb-4" outlined>
                <v-card-title>
                  <v-icon left color="primary">mdi-account</v-icon>
                  User & Transaction Details
                </v-card-title>
                <v-card-text>
                  <v-row>
                    <v-col sm="4">
                      <v-autocomplete
                        v-model="form.user_id"
                        :items="userItems"
                        :item-text="getUserDisplayText"
                        item-value="id"
                        outlined
                        label="User"
                        prepend-inner-icon="mdi-account"
                        :disabled="!isEditMode"
                        :readonly="!isEditMode"
                        :clearable="isEditMode"
                        :error-messages="errors.user_id"
                        hide-details="auto"
                      ></v-autocomplete>
                    </v-col>
                    <v-col sm="4">
                      <v-autocomplete
                        v-model="form.transaction_purpose_id"
                        :items="purposeItems"
                        item-text="description"
                        item-value="id"
                        outlined
                        label="Transaction Purpose"
                        prepend-inner-icon="mdi-tag"
                        :disabled="!isEditMode"
                        :readonly="!isEditMode"
                        :clearable="isEditMode"
                        :error-messages="errors.transaction_purpose_id"
                        hide-details="auto"
                      ></v-autocomplete>
                    </v-col>
                    <v-col sm="4">
                      <v-autocomplete
                        v-model="form.transaction_type"
                        :items="typeItems"
                        item-text="text"
                        item-value="value"
                        outlined
                        label="Transaction Type *"
                        prepend-inner-icon="mdi-swap-horizontal"
                        :disabled="!isEditMode"
                        :readonly="!isEditMode"
                        :error-messages="errors.transaction_type"
                        hide-details="auto"
                      ></v-autocomplete>
                    </v-col>
                    <v-col sm="4">
                      <v-text-field
                        v-model.number="form.amount"
                        outlined
                        label="Amount *"
                        prepend-inner-icon="mdi-currency-inr"
                        type="number"
                        min="1"
                        :disabled="!isEditMode"
                        :readonly="!isEditMode"
                        :error-messages="errors.amount"
                        hide-details="auto"
                      ></v-text-field>
                    </v-col>
            <v-col sm="4">
              <v-text-field
                v-model="form.code"
                outlined
                label="Code"
                prepend-inner-icon="mdi-qrcode"
                :error-messages="errors.code"
              ></v-text-field>
            </v-col>
            <v-col sm="4">
              <v-text-field
                v-model="form.merchant_id"
                outlined
                label="Merchant ID"
                prepend-inner-icon="mdi-store"
                :error-messages="errors.merchant_id"
              ></v-text-field>
            </v-col>
            <v-col sm="4">
              <v-text-field
                v-model="form.merchant_transaction_id"
                outlined
                label="Merchant Transaction ID"
                prepend-inner-icon="mdi-receipt"
                :error-messages="errors.merchant_transaction_id"
              ></v-text-field>
            </v-col>
            <v-col sm="4">
              <v-text-field
                v-model="form.transaction_id"
                outlined
                label="Transaction ID"
                prepend-inner-icon="mdi-identifier"
                :error-messages="errors.transaction_id"
              ></v-text-field>
            </v-col>
            <v-col sm="4">
              <v-text-field
                v-model="form.payment_id"
                outlined
                label="Payment ID"
                prepend-inner-icon="mdi-credit-card"
                :error-messages="errors.payment_id"
              ></v-text-field>
            </v-col>
            <v-col sm="4">
              <v-text-field
                v-model="form.ute"
                outlined
                label="UTE"
                prepend-inner-icon="mdi-identifier"
                :error-messages="errors.ute"
              ></v-text-field>
            </v-col>
            <v-col sm="4">
              <v-text-field
                v-model="form.account_type"
                outlined
                label="Account Type"
                prepend-inner-icon="mdi-bank"
                :error-messages="errors.account_type"
              ></v-text-field>
            </v-col>
                    <v-col sm="4">
                      <v-autocomplete
                        v-model="form.status"
                        :items="statusItems"
                        item-text="text"
                        item-value="value"
                        outlined
                        label="Status *"
                        prepend-inner-icon="mdi-check-circle"
                        :disabled="!isEditMode"
                        :readonly="!isEditMode"
                        :error-messages="errors.status"
                        hide-details="auto"
                      ></v-autocomplete>
                    </v-col>
                    <v-col sm="4">
                      <v-switch
                        class="mt-0 ml-5"
                        v-model="form.is_active"
                        inset
                        :disabled="!isEditMode"
                        :label="`Is Active: ${form.is_active ? 'YES' : 'NO'}`"
                      ></v-switch>
                    </v-col>
                  </v-row>
                </v-card-text>
              </v-card>

              <!-- Payment Information -->
              <v-card class="mb-4" outlined>
                <v-card-title>
                  <v-icon left color="success">mdi-credit-card</v-icon>
                  Payment Information
                </v-card-title>
                <v-card-text>
                  <v-row>

                    <v-col sm="4">
                      <v-text-field
                        v-model="form.code"
                        outlined
                        label="Code"
                        prepend-inner-icon="mdi-qrcode"
                        :disabled="!isEditMode"
                        :readonly="!isEditMode"
                        :error-messages="errors.code"
                        hide-details="auto"
                      ></v-text-field>
                    </v-col>
                    <v-col sm="4">
                      <v-text-field
                        v-model="form.merchant_id"
                        outlined
                        label="Merchant ID"
                        prepend-inner-icon="mdi-store"
                        :disabled="!isEditMode"
                        :readonly="!isEditMode"
                        :error-messages="errors.merchant_id"
                        hide-details="auto"
                      ></v-text-field>
                    </v-col>
                    <v-col sm="4">
                      <v-text-field
                        v-model="form.merchant_transaction_id"
                        outlined
                        label="Merchant Transaction ID"
                        prepend-inner-icon="mdi-receipt"
                        :disabled="!isEditMode"
                        :readonly="!isEditMode"
                        :error-messages="errors.merchant_transaction_id"
                        hide-details="auto"
                      ></v-text-field>
                    </v-col>
                    <v-col sm="4">
                      <v-text-field
                        v-model="form.transaction_id"
                        outlined
                        label="Transaction ID"
                        prepend-inner-icon="mdi-identifier"
                        :disabled="!isEditMode"
                        :readonly="!isEditMode"
                        :error-messages="errors.transaction_id"
                        hide-details="auto"
                      ></v-text-field>
                    </v-col>
                    <v-col sm="4">
                      <v-text-field
                        v-model="form.payment_id"
                        outlined
                        label="Payment ID"
                        prepend-inner-icon="mdi-credit-card"
                        :disabled="!isEditMode"
                        :readonly="!isEditMode"
                        :error-messages="errors.payment_id"
                        hide-details="auto"
                      ></v-text-field>
                    </v-col>
                    <v-col sm="4">
                      <v-text-field
                        v-model="form.ute"
                        outlined
                        label="UTE"
                        prepend-inner-icon="mdi-identifier"
                        :disabled="!isEditMode"
                        :readonly="!isEditMode"
                        :error-messages="errors.ute"
                        hide-details="auto"
                      ></v-text-field>
                    </v-col>
                    <v-col sm="4">
                      <v-text-field
                        v-model="form.account_type"
                        outlined
                        label="Account Type"
                        prepend-inner-icon="mdi-bank"
                        :disabled="!isEditMode"
                        :readonly="!isEditMode"
                        :error-messages="errors.account_type"
                        hide-details="auto"
                      ></v-text-field>
                    </v-col>
                  </v-row>
                </v-card-text>
              </v-card>
              
              <!-- Form Actions Footer -->
              <v-divider></v-divider>
              <v-card-actions class="form-actions pa-2" v-show="isEditMode">
                <v-row class="w-100 ma-0" align="center">
                  <v-col cols="12" md="6" class="pa-0">
                    <div class="d-flex align-center">
                      <span class="text-caption grey--text">
                        All required fields must be completed
                      </span>
                    </div>
                  </v-col>
                  
                  <v-col cols="12" md="6" class="pa-0">
                    <div class="d-flex justify-end">
                      <v-btn
                        color="grey"
                        outlined
                        class="mr-3 action-button"
                        @click="$router.back()"
                        :disabled="isLoading"
                      >
                        Cancel
                      </v-btn>
                      <v-btn
                        color="primary"
                        class="action-button"
                        @click="save"
                        :loading="isLoading"
                        :disabled="isLoading"
                      >
                        <v-icon left>mdi-content-save</v-icon>
                        Update Transaction
                      </v-btn>
                    </div>
                  </v-col>
                </v-row>
              </v-card-actions>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

        <!-- Loading State -->
        <v-row v-if="isLoading">
          <v-col cols="12">
            <v-card elevation="3" class="form-card">
              <v-card-text class="py-12 text-center">
                <health-loader message="Loading transaction details..." />
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
    </v-container>
  </v-main>
</template>
<script>
import axios from "@/axios";
export default {
  name: "TransactionEditPage",
  data() {
    return {
      form: {},
      errors: {},
      isLoading: false,
      userItems: [],
      purposeItems: [],
      typeItems: [
        { id: "DEBIT", text: "DEBIT", value: false },
        { id: "CREDIT", text: "CREDIT", value: true },
      ],
      statusItems: [
        { id: "FAILED", text: "FAILED", value: false },
        { id: "SUCCESS", text: "SUCCESS", value: true },
      ],
      isEditMode: false,
    };
  },
  mounted() {
    this.getMasters();
    this.getData();
  },
  methods: {
    toggleEditMode() {
      this.isEditMode = !this.isEditMode;
      // Show a message to the user about the mode change
      if (this.isEditMode) {
        this.$toast.success("Edit mode enabled. You can now modify the form.");
      } else {
        this.$toast.info("Read-only mode enabled. Form is now protected from changes.");
      }
    },

    async getMasters() {
      try {
        const response = await axios.get(`masters?context=transactions`);
        const masters = response.data;

        // Users
        if (masters.users) {
          this.userItems = masters.users;
        }

        // Transaction Purposes (from ValueList - use description field)
        if (masters.transaction_purposes) {
          this.purposeItems = masters.transaction_purposes.map(purpose => ({
            id: purpose.id,
            name: purpose.description,
            value: purpose.id,
            text: purpose.description
          }));
        }
      } catch (error) {
        console.log(error);
        if (error.response && error.response.status === 401) {
          window.location.href = "/auth/login";
        }
      }
    },

    async getData() {
      this.isLoading = true;
      let encryptedId = this.$route.params.transactionId;
      try {
        const response = await axios.get(`/transactions/${encryptedId}`);
        this.form = response.data.data;
        this.old_json = response.data.data;
        console.log(this.form);
      } catch (error) {
        console.error("Error fetching data:", error);
        if (error.response && error.response.status === 401) {
          window.location.href = "/auth/login";
        }
      }
      this.isLoading = false;
    },

    getUserDisplayText(user) {
      if (user.position) {
        return `${user.user_name} (${user.position.name})`;
      }
      return user.user_name;
    },

    async save() {
      try {
        this.isLoading = true;
        this.errors = {};

        await axios.patch(
          `/transactions/${this.$route.params.transactionId}`,
          this.form
        );

        await this.saveUserTimestamp(this.old_json, this.form);
        this.isLoading = false;
        this.$router.push("/transactions");
      } catch (error) {
        this.isLoading = false;
        if (error.response && error.response.data.errors) {
          this.errors = error.response.data.errors;
        } else {
          console.error("Error updating transaction:", error);
          if (error.response && error.response.status === 401) {
            window.location.href = "/auth/login";
          }
        }
      }
    },
  },
};
</script>

<style scoped>
/* Professional Page Header */
.page-header-card {
  background: linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%);
  border-left: 4px solid var(--v-primary-base);
}

.page-title {
  font-size: 1.25rem;
  font-weight: 600;
  line-height: 1.1;
  margin-bottom: 0.15rem;
}

.page-subtitle {
  font-size: 0.75rem;
  line-height: 1.2;
}

.back-button {
  min-width: 100px;
  height: 32px;
  font-weight: 500;
  text-transform: none;
  font-size: 0.75rem;
}

/* Professional Form Card */
.form-card {
  background: #ffffff;
  border-radius: 8px;
  overflow: hidden;
}

.form-header {
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border-bottom: 1px solid #dee2e6;
}

.form-title {
  font-size: 1rem;
  font-weight: 600;
  color: var(--v-primary-base);
  margin: 0;
}

.form-content {
  background: #ffffff;
}

/* Form Actions */
.form-actions {
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border-top: 1px solid #dee2e6;
}

.action-button {
  min-width: 90px;
  height: 32px;
  font-weight: 500;
  text-transform: none;
  border-radius: 4px;
  font-size: 0.75rem;
}
</style>